import { QMulti } from '@educraft/base';
import {
  PdfGen,
  PdfUnit,
  PdfRect,
  PdfBrush,
  ETextAlign,
  PdfSize,
  PdfColor,
  PdfFont,
} from '../../pdf/pdf';
import { ILayoutMultiGen } from '../layoutMulti';
import { OptsMulti } from '../optsMulti';
import { GUtil } from '../../pdf/gutil';
import { genMulti } from '../../core/genHelper';
import { GLabel, GTuple } from '../../pdf/glabel';
import {
  ELogicoVariant,
  ELogicoMode,
  LogicoData,
  drawLogico,
  drawLogicoCircle,
  drawLogicoRect,
  genMap,
  logicoCount,
} from '../../pdf/board/logico';
import '../../pdf/draw/primitives';
import '../../pdf/draw/string';
import '../../pdf/draw/helpers';

const CIRCLE_SIZE = PdfUnit.FromMillimeters(4);
const CIRCLE_MARGIN = PdfUnit.FromMillimeters(1);
const CIRCLE_D = CIRCLE_SIZE + 2 * CIRCLE_MARGIN; // total horizontal space for circle

import { IItemCount } from '@educraft/base';

/**
 * Base class for Logico puzzle generators (Multi version).
 */
abstract class LogicoMultiBase implements ILayoutMultiGen, IItemCount {
  constructor(
    protected readonly variant: ELogicoVariant,
    protected readonly mode: ELogicoMode
  ) {}

  countDefault(): number {
    return 1;
  }
  countMin(): number {
    return 1;
  }
  countMax(): number {
    return 10;
  }

  public async generate(pdf: PdfGen, opts: OptsMulti): Promise<void> {
    const itemsPerPage = logicoCount(this.variant);
    const totalPuzzles = this.countDefault();
    const gen = genMulti(opts.data, opts.generator, itemsPerPage * totalPuzzles, true, true);

    for (let i = 0; i < totalPuzzles; i++) {
      const pageIndex = pdf.gs.length;
      pdf.addPage(); // Question page
      pdf.addPage(); // Answer page

      const currentGen = gen.slice(i * itemsPerPage, (i + 1) * itemsPerPage);

      // labels[i].right = correct answer for question i → used by default answer column drawing
      const labels: GTuple[] = currentGen.map((q) => ({
        left: GLabel.fromText(q.texts.join(' ')),
        right: GLabel.fromText(q.answers[q.answerIndex]),
      }));

      const map = genMap(currentGen.length, opts.data.random);

      // options[j][k] = circle color index for answer option k of question j.
      const options: number[][] = currentGen.map((q, j) => {
        const correctColor = map[j];
        const others = Array.from({ length: itemsPerPage }, (_, k) => k).filter(
          (k) => k !== correctColor
        );
        // Fisher-Yates shuffle of other colors
        for (let k = others.length - 1; k > 0; k--) {
          const r = opts.data.random.next(0, k + 1);
          [others[k], others[r]] = [others[r], others[k]];
        }
        return [...others.slice(0, q.answerIndex), correctColor, ...others.slice(q.answerIndex)];
      });

      let {baseFont} = GUtil;
      if (this.variant === ELogicoVariant.Maximo) {
        baseFont = new PdfFont(PdfFont.FONT_NAME, 16);
      }
      let font = baseFont;
      let answerFont = baseFont;

      drawLogico(pdf, this.variant, this.mode, labels, map, {
        drawQuestion: (data: LogicoData) => {
          if (data.index < 0 || data.index >= currentGen.length) return;
          const q = currentGen[data.index];
          const qOptions = options[data.index];

          const totalRows = q.texts.length + q.answers.length;
          if (q.texts.length === 0) return;
          const sz = data.page.measureString(q.texts[0], font);

          const limit = sz.height * totalRows;

          if (data.rect.height >= limit) {
            const rowH = data.rect.height / totalRows;
            const textH = rowH * q.texts.length;

            const textRect = new PdfRect(data.rect.x, data.rect.y, data.rect.width, textH);
            data.page.drawStrings(
              q.texts,
              font,
              PdfBrush.Black,
              null,
              textRect,
              ETextAlign.CenterLeft
            );

            const f = data.page.adjustFont(data.rect.width - rowH, q.answers, baseFont);

            for (let j = 0; j < q.answers.length; j++) {
              const optY = data.rect.y + textH + j * rowH;
              drawLogicoCircle(
                data.page,
                data.variant,
                qOptions[j],
                new PdfRect(data.rect.x, optY, rowH, rowH)
              );
              data.page.drawString(
                q.answers[j],
                f,
                PdfBrush.Black,
                null,
                new PdfRect(data.rect.x + rowH, optY, data.rect.width - rowH, rowH),
                ETextAlign.CenterLeft
              );
            }
          } else {
            const halfH = data.rect.height / 2;
            const r0 = new PdfRect(data.rect.x, data.rect.y, data.rect.width, halfH);
            const optY = data.rect.y + halfH;
            const optW = data.rect.width / q.answers.length;

            data.page.drawStrings(q.texts, font, PdfBrush.Black, null, r0, ETextAlign.Center);

            const f = data.page.adjustFont(optW - CIRCLE_D, q.answers, baseFont);

            for (let j = 0; j < q.answers.length; j++) {
              const optX = data.rect.x + j * optW;
              if (optW > CIRCLE_D) {
                const cx = optX + CIRCLE_MARGIN + CIRCLE_SIZE / 2;
                const cy = optY + halfH / 2;
                drawLogicoCircle(
                  data.page,
                  data.variant,
                  qOptions[j],
                  new PdfRect(cx - CIRCLE_SIZE / 2, cy - CIRCLE_SIZE / 2, CIRCLE_SIZE, CIRCLE_SIZE)
                );
                data.page.drawString(
                  q.answers[j],
                  f,
                  PdfBrush.Black,
                  null,
                  new PdfRect(optX + CIRCLE_D, optY, optW - CIRCLE_D, halfH),
                  ETextAlign.CenterLeft
                );
              }
            }
          }
        },

        drawAnswer: (data: LogicoData) => {
          if (data.index < 0 || data.index >= currentGen.length) return;

          if (data.isFront) {
            data.page.drawRectangle(
              GUtil.basePen,
              new PdfBrush(PdfColor.fromCmyk(0, 0, 0, 0.1)),
              data.rect
            );
          } else {
            const g = currentGen[data.index];
            const colorIdx = options[data.index][g.answerIndex];

            drawLogicoRect(data.page, data.variant, colorIdx, data.rect);
            const label = labels[data.index].right;
            const text = label.getText();
            const marginMm = data.rect.width > PdfUnit.FromMillimeters(30) ? 1 : 0.5;
            const innerRect = data.rect.deflate(PdfUnit.FromMillimeters(marginMm));
            data.page.drawStringMulti(
              text,
              answerFont,
              PdfBrush.Black,
              null,
              innerRect,
              ETextAlign.Center
            );
            data.page.drawRectangle(GUtil.basePen, null, data.rect);
          }
        },

        calcQuestionSize: (size) => {
          const allTexts = [
            ...currentGen.flatMap((q) => q.texts),
            ...currentGen.flatMap((q) => q.answers),
          ];
          font = pdf.getPage(pageIndex).adjustFontMulti(
            allTexts.map((t) => [t]),
            baseFont,
            new PdfSize(size.width - CIRCLE_D, size.height)
          );
        },

        calcAnswerSize: (size: PdfSize) => {
          const allAnswerTexts = labels.map((l) => [l.right.getText()]);
          answerFont = pdf
            .getPage(pageIndex)
            .adjustFontMulti(
              allAnswerTexts,
              baseFont,
              new PdfSize(
                size.width - PdfUnit.FromMillimeters(2),
                size.height - PdfUnit.FromMillimeters(2)
              )
            );
        },

        pageIndex,
        drawCircle: false,
        data: opts.data,
      });
    }
  }
}

// Piccolo Multi variants
export class Piccolo_Multi__Base extends LogicoMultiBase {
  constructor() {
    super(ELogicoVariant.Piccolo, ELogicoMode.Rows);
  }
}

export class Piccolo_Multi__Mini extends LogicoMultiBase {
  constructor() {
    super(ELogicoVariant.Piccolo, ELogicoMode.Mini);
  }
}

export class Piccolo_Multi__ZigZag1 extends LogicoMultiBase {
  constructor() {
    super(ELogicoVariant.Piccolo, ELogicoMode.ZigZag1);
  }
}

export class Piccolo_Multi__ZigZag2 extends LogicoMultiBase {
  constructor() {
    super(ELogicoVariant.Piccolo, ELogicoMode.ZigZag2);
  }
}

export class Piccolo_Multi__Diagonal extends LogicoMultiBase {
  constructor() {
    super(ELogicoVariant.Piccolo, ELogicoMode.Diagonal);
  }
}

export class Piccolo_Multi__PaperA5 extends LogicoMultiBase {
  constructor() {
    super(ELogicoVariant.Piccolo, ELogicoMode.PaperA5);
  }
}

// Primo Multi variants
export class Primo_Multi__Mini extends LogicoMultiBase {
  constructor() {
    super(ELogicoVariant.Primo, ELogicoMode.Mini);
  }
}

export class Primo_Multi__Base extends LogicoMultiBase {
  constructor() {
    super(ELogicoVariant.Primo, ELogicoMode.Rows);
  }
}

export class Primo_Multi__ZigZag1 extends LogicoMultiBase {
  constructor() {
    super(ELogicoVariant.Primo, ELogicoMode.ZigZag1);
  }
}

export class Primo_Multi__ZigZag2 extends LogicoMultiBase {
  constructor() {
    super(ELogicoVariant.Primo, ELogicoMode.ZigZag2);
  }
}

export class Primo_Multi__Diagonal extends LogicoMultiBase {
  constructor() {
    super(ELogicoVariant.Primo, ELogicoMode.Diagonal);
  }
}

// Maximo Multi variants
export class Maximo_Multi__Base extends LogicoMultiBase {
  constructor() {
    super(ELogicoVariant.Maximo, ELogicoMode.Rows);
  }
}

export class Maximo_Multi__ZigZag1 extends LogicoMultiBase {
  constructor() {
    super(ELogicoVariant.Maximo, ELogicoMode.ZigZag1);
  }
}

export class Maximo_Multi__ZigZag2 extends LogicoMultiBase {
  constructor() {
    super(ELogicoVariant.Maximo, ELogicoMode.ZigZag2);
  }
}

export class Maximo_Multi__Mini extends LogicoMultiBase {
  constructor() {
    super(ELogicoVariant.Maximo, ELogicoMode.Mini);
  }
}

export class Maximo_Multi__Diagonal extends LogicoMultiBase {
  constructor() {
    super(ELogicoVariant.Maximo, ELogicoMode.Diagonal);
  }
}
